import type {
  AdminInvoiceReportsSearchFormData,
  AdminInvoiceReportsSearchQuery,
} from '@/features/admin_invoice_reports/types';
import { toISODateString } from 'date-util';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';

export const getDefaultFormValues = (): AdminInvoiceReportsSearchFormData => ({
  query: '',
  number: '',
  status: null,
  payment_status: null,
  mf_file: null,
  applicant_ids: [],
  applicant_department_ids: [],
  department_ids: [],
  viewer_ids: [],
  ap_counterparty_ids: [],
  ap_payee_ids: [],
  payment_method: null,
  total_amount_from: '',
  total_amount_to: '',
  created_date: ['', ''],
  submitted_date: ['', ''],
  approved_date: ['', ''],
  book_date: ['', ''],
  due_date: ['', ''],
  payment_date: ['', ''],
  sort_fields: undefined,
  sort_orders: undefined,
  page: 1,
});

/**
 * Converts react-hook-form state into the API query shape.
 */
export const convertFormDataToApiQuery = (
  formData: AdminInvoiceReportsSearchFormData
): AdminInvoiceReportsSearchQuery => {
  const query: AdminInvoiceReportsSearchQuery = {};

  if (formData.query) query.query = formData.query;
  if (formData.number) query.number = formData.number;
  if (formData.status) query.status = formData.status;
  if (formData.applicant_ids?.length)
    query.office_member_ids = formData.applicant_ids;
  if (formData.viewer_ids?.length)
    query.viewer_office_member_ids = formData.viewer_ids;
  if (formData.ap_counterparty_ids.length)
    query.ap_counterparty_ids = formData.ap_counterparty_ids;
  if (formData.ap_payee_ids?.length) query.ap_payee_ids = formData.ap_payee_ids;
  if (formData.applicant_department_ids?.length)
    query.dept_ids = formData.applicant_department_ids;
  if (formData.department_ids?.length)
    query.report_dept_ids = formData.department_ids;
  if (formData.total_amount_from)
    query.total_amount_from = formData.total_amount_from;
  if (formData.total_amount_to)
    query.total_amount_to = formData.total_amount_to;
  if (formData.payment_method) query.payment_method = formData.payment_method;
  if (formData.payment_status) query.payment_status = formData.payment_status;
  if (formData.mf_file) query.mf_file = formData.mf_file;
  if (formData.page) query.page = formData.page;

  // Date ranges: convert Dayjs (or ISO string) to ISO date string
  if (formData.created_date?.length === 2) {
    if (formData.created_date[0])
      query.created_at_from =
        toISODateString(formData.created_date[0]) || undefined;
    if (formData.created_date[1])
      query.created_at_to =
        toISODateString(formData.created_date[1]) || undefined;
  }
  if (formData.submitted_date?.length === 2) {
    if (formData.submitted_date[0])
      query.submitted_at_from =
        toISODateString(formData.submitted_date[0]) || undefined;
    if (formData.submitted_date[1])
      query.submitted_at_to =
        toISODateString(formData.submitted_date[1]) || undefined;
  }
  if (formData.approved_date?.length === 2) {
    if (formData.approved_date[0])
      query.approved_at_from =
        toISODateString(formData.approved_date[0]) || undefined;
    if (formData.approved_date[1])
      query.approved_at_to =
        toISODateString(formData.approved_date[1]) || undefined;
  }
  if (formData.book_date?.length === 2) {
    if (formData.book_date[0])
      query.book_date_from =
        toISODateString(formData.book_date[0]) || undefined;
    if (formData.book_date[1])
      query.book_date_to = toISODateString(formData.book_date[1]) || undefined;
  }
  if (formData.due_date?.length === 2) {
    if (formData.due_date[0])
      query.due_date_from = toISODateString(formData.due_date[0]) || undefined;
    if (formData.due_date[1])
      query.due_date_to = toISODateString(formData.due_date[1]) || undefined;
  }
  if (formData.payment_date?.length === 2) {
    if (formData.payment_date[0])
      query.payment_date_from =
        toISODateString(formData.payment_date[0]) || undefined;
    if (formData.payment_date[1])
      query.payment_date_to =
        toISODateString(formData.payment_date[1]) || undefined;
  }

  if (formData.sort_fields && formData.sort_orders) {
    query.sort_fields = formData.sort_fields;
    query.sort_orders = formData.sort_orders;
  }

  return query;
};

interface UseSearchFormProps {
  form: UseFormReturn<AdminInvoiceReportsSearchFormData>;
}

export const useSearchForm = ({ form }: UseSearchFormProps) => {
  const handleReset = useCallback(() => {
    form.reset(getDefaultFormValues());
    form.clearErrors();
  }, [form]);

  return { handleReset };
};
